import React, { useEffect, useMemo, useRef, useState } from 'react';
import { CircularProgress, DialogActions, withStyles } from '@material-ui/core';
import flowRight from 'lodash.flowright';
import QRCode from 'qrcode';
import { connect } from 'react-redux';
import clsx from 'clsx';

import withSettings from '../../../utils/withSettings';
import styles from '../styles/orderESimcard';
import { translate } from '../../../i18n/I18nProvider';
import {
  isDefined,
  isStringEmpty,
  isUndefined
} from '../../../utils/isDefined';
import createPdf from '../actions/createPdf';
import PrimaryButton from '../../../partials/customButtons/PrimaryButton';
import SecondaryButton from '../../../partials/customButtons/SecondaryButton';
import closeSwapCard from '../actions/closeSwapCard';
import { URLS } from '../../../consts/endpoints';
import { getData } from '../../../utils/http';
import { findESimStatus } from '../../../consts/initialValues';
import Optional from '../../../utils/optional';
import getSubscriptions from '../../editSimcard/actions/getSubscription';
import findIccFromSub from '../../../utils/findIccFromSub';
import RESPONSE_CODES from '../../../consts/responseCodes';

const OrderSummary = ({
  settings,
  classes,
  activationResponse,
  closeSwapCard,
  simcard,
  open
}) => {
  const [src, setSrc] = useState('');
  const [hideCode, setHideCode] = useState(true);
  const [status, setStatus] = useState(void 0);
  const [transactionId, setTransactionId] = useState(null);
  const [isTransactionComplete, setIsTransactionComplete] = useState(false);
  const statusTimeoutRef = useRef(null);
  const transactionTimeoutRef = useRef(null);
  const iccRetryTimeoutRef = useRef(null);

  const number = useMemo(() => simcard.msisdn, [simcard]);

  useEffect(() => {
    if (isDefined(activationResponse?.['ac_token'])) {
      QRCode.toDataURL(activationResponse['ac_token']).then(data => {
        setSrc(data);
      });
    }
    if (isDefined(activationResponse?.transaction_id)) {
      setTransactionId(activationResponse?.transaction_id);
    }
  }, [activationResponse]);

  useEffect(() => {
    const clearTimers = () => {
      if (statusTimeoutRef.current) {
        clearTimeout(statusTimeoutRef.current);
        statusTimeoutRef.current = null;
      }
      if (transactionTimeoutRef.current) {
        clearTimeout(transactionTimeoutRef.current);
        transactionTimeoutRef.current = null;
      }
      if (iccRetryTimeoutRef.current) {
        clearTimeout(iccRetryTimeoutRef.current);
        iccRetryTimeoutRef.current = null;
      }
    };

    const getTransactionStatus = async () => {
      const transaction = await getData(
        `${URLS.QUOTE_CLIENTS}/${simcard.client_id}/transactions/${transactionId}`
      );

      if (transaction?.status === 'COMPLETED') {
        setIsTransactionComplete(true);
        return true;
      }

      return false;
    };

    const checkTransaction = () => {
      transactionTimeoutRef.current = setTimeout(async () => {
        if (
          !isDefined(transactionId) ||
          isTransactionComplete ||
          open === false
        )
          return;

        const completed = await getTransactionStatus();

        if (!completed) {
          checkTransaction();
        }
      }, 5000);
    };

    const resourceIccRequest = async icc => {
      if (open === false || !isStringEmpty(src) || isUndefined(icc)) return;

      try {
        const { data } = await getData({
          options: {
            url: `${URLS.QUOTE_CLIENTS}/${simcard.client_id}/resources/search?icc=${icc}`
          },
          errorsToPass: [RESPONSE_CODES.FORBIDDEN]
        });

        if (isStringEmpty(src) && isDefined(data?.activationCode)) {
          QRCode.toDataURL(data.activationCode).then(data => {
            setSrc(data);
          });
          setStatus({ icc_id: icc });
          setHideCode(false);
        }
      } catch (error) {
        if (error?.response?.status === RESPONSE_CODES.FORBIDDEN) {
          if (open === false) return;

          iccRetryTimeoutRef.current = setTimeout(() => {
            resourceIccRequest(icc);
          }, 10000);
        }
      }
    };

    const getSub = async () => {
      if (!isStringEmpty(src)) return;

      const subscription = await getSubscriptions(
        simcard.msisdn,
        simcard.client_id
      );

      const newIcc = await findIccFromSub(subscription, simcard.msisdn);

      await resourceIccRequest(newIcc);
    };

    if (!isTransactionComplete && isDefined(transactionId)) {
      checkTransaction();
    }

    if (isTransactionComplete) {
      getSub();
    }

    return clearTimers;

    //Disabled the check status functions because right now we don't get correct status

    //  const getStatus = async () => {
    //   try {
    //     const esimStatus = await getData(
    //       `${URLS.CARRIER}/cell_number/${number}/esim_status`
    //     );
    //     setStatus(esimStatus);

    //     if (isStringEmpty(src) && isDefined(esimStatus?.['ac_token'])) {
    //       QRCode.toDataURL(esimStatus['ac_token']).then(data => {
    //         setSrc(data);
    //       });
    //     }

    //     if (
    //       esimStatus?.state === 'ENABLED' ||
    //       esimStatus?.state === 'DELETED'
    //     ) {
    //       setHideCode(true);
    //     } else {
    //       setHideCode(false);
    //     }

    //     return esimStatus?.state;
    //   } catch (error) {
    //     setStatus({ state: 'ERROR' });
    //     console.error('Error:', error);
    //     return 'ERROR';
    //   }
    // };

    // const checkStatus = () => {
    //   statusTimeoutRef.current = setTimeout(async () => {
    //     if (
    //       !isTransactionComplete ||
    //       status?.state === 'ENABLED' ||
    //       open === false
    //     )
    //       return;

    //     const state = await getStatus();

    //     if (state !== 'ENABLED') {
    //       checkStatus();
    //     }
    //   }, 10000);
    // };

    // if (isTransactionComplete && status?.state !== 'ENABLED') {
    //   checkStatus();
    // }
  }, [
    number,
    src,
    status?.state,
    isTransactionComplete,
    transactionId,
    simcard.client_id,
    open
  ]);

  const onSaveClick = () => {
    return createPdf({ number, src, icc: status?.icc_id });
  };

  return (
    <div className={classes.dialogContent}>
      <div className={classes.titleRow}>
        {translate('SWAP_SIMCARD.ESIM_SUMMARY_1')}
      </div>
      <div className={classes.row}>
        {`${translate('SWAP_SIMCARD.ESIM_SUMMARY_2')} ${number}`}
      </div>
      <div className={classes.row}>
        {translate('SWAP_SIMCARD.ESIM_SUMMARY_3')}
      </div>
      <div className={clsx(classes.row, classes.dialogContent)}>
        <div>{translate('SWAP_SIMCARD.ESIM_SUMMARY_4')} </div>
        <div>{translate('SWAP_SIMCARD.ESIM_SUMMARY_5')}</div>
      </div>
      <div className={clsx(classes.row, classes.bold)}>
        <div>{translate('SWAP_SIMCARD.ESIM_SUMMARY_7_5')}</div>
        {translate('SWAP_SIMCARD.ESIM_SUMMARY_7')}
      </div>

      {/* Disabled the check status functions because right now we don't get correct status */}
      {/* {isDefined(status) && (
        <div className={classes.boldRow}>{`${translate('CURRENT')} ${translate(
          'ESIM_STATUS'
        )} : ${Optional(findESimStatus(status?.state)).or('')}`}</div>
      )} */}

      {!status && (
        <div className={classes.loaderContainer}>
          <CircularProgress />
        </div>
      )}
      {!hideCode && (
        <div className={classes.qrCodeContainer}>
          <img src={src} alt="icon" className={classes.icon}></img>
          <div
            className={classes.row}
            style={{ color: settings.colors.modal_header }}
          >
            {`${translate('SWAP_SIMCARD.ESIM_SUMMARY_6')} ${number}`}
            {isDefined(status?.icc_id) && (
              <div>{`icc : ${status?.icc_id}`}</div>
            )}
          </div>
        </div>
      )}
      <div className={classes.infoRow}>{translate('SWAP_SIMCARD.ESIM_5')}</div>
      <DialogActions className={classes.footer}>
        {!hideCode && (
          <PrimaryButton onClick={onSaveClick}>
            {translate('SWAP_SIMCARD.SAVE_PDF')}
          </PrimaryButton>
        )}

        <SecondaryButton onClick={closeSwapCard}>
          {translate('BUTTON.CLOSE')}
        </SecondaryButton>
      </DialogActions>
    </div>
  );
};

const mapStatesToProps = ({ swapSimcard }) => {
  return {
    ...swapSimcard
  };
};

const mapDispatchToProps = {
  closeSwapCard
};

export default flowRight(
  withSettings,
  connect(mapStatesToProps, mapDispatchToProps),
  withStyles(styles)
)(OrderSummary);
